import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import profileService from '../../services/profileService';
import friendService from '../../services/friendService';
import storyService from '../../services/storyService';
import { decodePortalPage } from '../../utils/portalPageModel';
import { webOrigin } from '../../utils/brand';
import { RING } from '../wall/wallTokens';
import { useArmed } from '../club/clubUi';
import PortalEditSheet from '../introduction/PortalEditSheet';
import ReportSheet from '../moderation/ReportSheet';
import { introDaysLeft } from './reelRing';
import { introCopy, joinedLabel, mutualLine } from '../introduction/introductionModel';

/**
 * An introduction portal, as the portal viewer draws it (signup v3.1 handoff, Oct 29 2026).
 *
 * `StoryViewer` mounts this for a reel's `intro` item, so it lives in the same shell as every other
 * Portal: full screen on a phone, a 440px panel on desktop. It is a summary of the person's Portal
 * Page, read through the public profile so their privacy settings apply here too: the seven-day
 * bar, the photo, handle, vibe check and facts, the anthem, + ADD FRIEND / SAY HI, mutuals, MUSIC
 * THAT MOVES ME, STRENGTHS and DOWN FOR. The body scrolls inside so it fits any phone height.
 *
 * Adding someone keeps this open ("leaves your rail when you close this"); `onRailChanged` tells
 * the viewer to refresh the rail once it closes. Your own opens exactly as others see it, with
 * EDIT PORTAL in place of + ADD FRIEND and SAY HI inert as a preview.
 */
const SERIF = "'Times New Roman', Times, serif";
const CARD = { display: 'flex', flexDirection: 'column', background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F' };
const SMALL = { fontSize: 9, fontWeight: 800, letterSpacing: '.16em', color: '#8A8A8A' };
const CHIP = { display: 'flex', alignItems: 'center', height: 28, padding: '0 12px', borderRadius: 999, fontSize: 9, fontWeight: 800, letterSpacing: '.12em' };
const CIRCLE = { flexShrink: 0, width: 40, height: 40, border: 0, borderRadius: '50%', background: '#161616', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };

export default function IntroductionPortal({ reel, intro, onClose, onRailChanged, onSkip }) {
  const navigate = useNavigate();
  const own = !!reel.isOwn;
  const [page, setPage] = useState(null);
  const [added, setAdded] = useState(false);
  const [saidHi, setSaidHi] = useState(false);
  const [note, setNote] = useState(null);
  const [editing, setEditing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const { confirm, armed } = useArmed();

  const load = useCallback(async () => {
    const r = await profileService.getProfileByUserId(reel.authorUserId);
    if (r?.success && r.data) setPage(decodePortalPage(r.data));
  }, [reel.authorUserId]);
  useEffect(() => { load(); }, [load]);

  const name = page?.name || reel.displayName || '';
  const first = name.trim().split(' ')[0] || name;
  const days = introDaysLeft(intro.expiresAtUtc);
  const copy = introCopy({ name, own, place: intro.city, days });
  const music = (page?.music || []).map((m) => (m || '').trim()).filter(Boolean);
  const strengths = page?.strengths || [];
  const downFor = page?.downFor || [];
  const nothingYet = own && page && !music.length && !strengths.length && !downFor.length;
  const facts = [intro.city, page?.occupation, `JOINED ${joinedLabel(intro.joinedAtUtc)}`].filter(Boolean).join(' · ').toUpperCase();
  const mutuals = mutualLine(intro.mutualNames, intro.mutualCount);

  const add = async () => {
    if (added) return;
    setAdded(true);
    setNote(null);
    const r = await friendService.sendFriendRequest(reel.authorUserId);
    if (r?.success === false) { setAdded(false); setNote(r.error || 'That request did not go through. Try again.'); return; }
    onRailChanged?.();
  };

  const sayHi = async () => {
    if (saidHi) return;
    setSaidHi(true);
    setNote(null);
    const { default: messageService } = await import('../../services/messageService');
    const thread = await messageService.createOrGetThread(reel.authorUserId, null, null, intro.id);
    const threadId = thread?.data?.id || thread?.data?.threadId;
    const sent = threadId ? await messageService.sendMessage(threadId, { body: 'Hi' }) : null;
    if (!sent?.success) { setSaidHi(false); setNote('That hi did not go through. Try again.'); }
  };

  const share = async () => {
    const url = `${webOrigin()}/${reel.handle || ''}`;
    try {
      if (navigator.share) await navigator.share({ url, title: copy.title });
      else { await navigator.clipboard?.writeText(url); setNote('Link copied.'); }
    } catch { /* the share sheet was closed */ }
  };

  const mute = async () => {
    await storyService.mute(reel.authorUserId);
    onRailChanged?.();
    onSkip?.();
  };

  const takeDown = async () => {
    if (!confirm('takeDown')) return;
    const r = await storyService.remove(intro.id);
    if (r?.success === false) { setNote(r.error || 'It could not be taken down. Try again.'); return; }
    onRailChanged?.();
    onClose?.();
  };

  const face = page?.photos?.find((p) => p.id === page.avatar)?.src || reel.avatarUrl;
  return (
    <div className="pk absolute inset-0 flex flex-col" data-testid="introduction-portal" style={{ background: '#000', color: '#fff', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div aria-hidden="true" data-testid="intro-days" style={{ display: 'flex', gap: 4, padding: '10px 16px 0' }}>
        {Array.from({ length: 7 }, (_, i) => <span key={i} style={{ flex: 1, height: 3, borderRadius: 999, background: i < days ? '#fff' : '#2A2A2A' }} />)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
        <button type="button" onClick={onClose} aria-label="Close" style={CIRCLE}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.16em' }}>{copy.title}</span>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: '#8A8A8A' }}>{copy.sub}</span>
        </span>
        <button type="button" onClick={share} aria-label="Share" style={CIRCLE}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" aria-hidden="true"><path d="M12 3v12M7 8l5-5 5 5M5 14v7h14v-7" /></svg>
        </button>
      </div>

      <div className="safe-bottom-inset" data-testid="intro-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 14px 20px', userSelect: 'text', WebkitUserSelect: 'text' }}>
        <section style={{ ...CARD, gap: 16, padding: 20, borderRadius: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <span className="pr-ring-anim" style={{ flexShrink: 0, width: 104, height: 104, boxSizing: 'border-box', border: '3px solid transparent', borderRadius: '50%', background: `linear-gradient(#111,#111) padding-box, ${RING} border-box` }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', border: '3px solid #111', boxSizing: 'border-box', background: '#1A1A1A' }}>
                {face ? <img src={face} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <span style={{ fontSize: 28, fontWeight: 800, color: '#3A3A3A' }}>{name.charAt(0).toUpperCase()}</span>}
              </span>
            </span>
            <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: 28, fontWeight: 800, letterSpacing: '.06em', lineHeight: 1, overflowWrap: 'anywhere' }}>{first.toUpperCase()}</span>
                {intro.isNew && <span style={{ display: 'flex', alignItems: 'center', height: 20, padding: '0 9px', borderRadius: 999, background: '#22ECFB', color: '#000', fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>NEW</span>}
              </span>
              {reel.handle && <span style={{ fontSize: 12, color: '#FF66C4' }}>@{reel.handle}</span>}
              {page?.vibe && <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 19, lineHeight: 1.3, textWrap: 'pretty' }}>“{page.vibe}”</span>}
              <span data-testid="intro-facts" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', lineHeight: 1.5, color: '#8A8A8A' }}>{facts}</span>
            </div>
          </div>

          {page?.anthem && <IntroAnthem anthem={page.anthem} />}

          {own ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setEditing(true)} data-testid="intro-edit" className="pk-btn pk-primary-on-black" style={{ flex: 1.4, height: 52, background: '#fff', color: '#000', fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>EDIT PORTAL</button>
              <span data-testid="intro-hi-preview" style={{ flex: 1, height: 52, borderRadius: 999, background: '#1F1F1F', color: '#8A8A8A', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>SAY HI</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={add} aria-pressed={added} data-testid="intro-add" className={`pk-btn ${added ? '' : 'pk-primary-on-black'}`} style={{ flex: 1.4, height: 52, background: added ? '#111' : '#fff', color: added ? '#22ECFB' : '#000', boxShadow: added ? 'inset 0 0 0 1px #1F1F1F' : 'none', fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>{added ? '✓ REQUEST SENT' : '+ ADD FRIEND'}</button>
                <button type="button" onClick={sayHi} data-testid="intro-hi" className="pk-btn" style={{ flex: 1, height: 52, background: '#1F1F1F', color: saidHi ? '#8A8A8A' : '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>{saidHi ? 'SAID HI' : 'SAY HI'}</button>
              </div>
              {added && <span data-testid="intro-leaves" style={{ fontSize: 10, lineHeight: 1.5, letterSpacing: '.06em', color: '#22ECFB' }}>Request sent. {first}’s introduction leaves your rail when you close this.</span>}
              {mutuals && (
                <span data-testid="intro-mutuals" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ display: 'flex' }}>
                    {(intro.mutualFaces || []).map((src) => <img key={src} src={src} alt="" style={{ width: 26, height: 26, marginRight: -8, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 2px #111' }} />)}
                  </span>
                  <span style={{ paddingLeft: 8, fontSize: 9, fontWeight: 700, letterSpacing: '.12em', lineHeight: 1.5, color: '#8A8A8A' }}>{mutuals}</span>
                </span>
              )}
            </>
          )}
          {note && <span role="status" style={{ fontSize: 11, lineHeight: 1.5, color: '#FFCCE6' }}>{note}</span>}
        </section>

        {music.length > 0 && (
          <section style={{ ...CARD, padding: '18px 20px 8px', borderRadius: 28 }}>
            <span style={{ ...SMALL, paddingBottom: 6 }}>MUSIC THAT MOVES ME</span>
            {music.map((m, i) => (
              <span key={m} style={{ display: 'flex', alignItems: 'baseline', gap: 14, padding: '12px 0', borderTop: i ? '1px solid #1F1F1F' : 0 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#6B6B6B' }}>{String(i + 1).padStart(2, '0')}</span>
                <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 20 }}>{m}</span>
              </span>
            ))}
          </section>
        )}

        {(strengths.length > 0 || downFor.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {strengths.length > 0 && (
              <section style={{ ...CARD, gap: 12, padding: 18, borderRadius: 24 }}>
                <span style={SMALL}>STRENGTHS</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{strengths.map((t) => <span key={t} style={{ ...CHIP, background: '#fff', color: '#000' }}>{t.toUpperCase()}</span>)}</div>
              </section>
            )}
            {downFor.length > 0 && (
              <section style={{ ...CARD, gap: 12, padding: 18, borderRadius: 24 }}>
                <span style={SMALL}>DOWN FOR</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{downFor.map((t) => <span key={t} style={{ ...CHIP, background: '#262626', color: '#fff' }}>{t.toUpperCase()}</span>)}</div>
              </section>
            )}
          </div>
        )}

        {nothingYet && (
          <section data-testid="intro-empty" style={{ ...CARD, gap: 8, padding: '18px 20px', borderRadius: 24, background: '#0B0B0B' }}>
            <span style={SMALL}>MUSIC · STRENGTHS · DOWN FOR</span>
            <span style={{ fontSize: 12, lineHeight: 1.55, color: '#8A8A8A' }}>These show here once you add them in your Portal Editor.</span>
          </section>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 18 }}>
          {!own && reel.handle && <button type="button" onClick={() => navigate(`/${reel.handle}`)} className="pk-btn pk-text-on-black" style={{ height: 36, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: '#8A8A8A' }}>FULL PAGE</button>}
          {!own && <button type="button" onClick={mute} data-testid="intro-mute" className="pk-btn pk-text-on-black" style={{ height: 36, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: '#8A8A8A' }}>MUTE {first.toUpperCase()}</button>}
          {!own && <button type="button" onClick={() => setReporting(true)} data-testid="intro-report" className="pk-btn pk-text-on-black" style={{ height: 36, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: '#8A8A8A' }}>REPORT</button>}
          {own && <button type="button" onClick={takeDown} data-testid="intro-take-down" className="pk-btn pk-text-on-black" style={{ height: 36, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: armed === 'takeDown' ? '#FF66C4' : '#8A8A8A' }}>{armed === 'takeDown' ? 'TAP AGAIN TO TAKE IT DOWN' : 'TAKE IT DOWN'}</button>}
        </div>
      </div>

      {reporting && (
        <ReportSheet
          userId={reel.authorUserId}
          displayName={reel.displayName}
          what="this introduction"
          storyId={intro?.id || null}
          onClose={() => setReporting(false)}
          onBlocked={() => { onRailChanged?.(); onSkip?.(); }}
        />
      )}
      {editing && <PortalEditSheet onClose={() => { setEditing(false); load(); }} />}
    </div>
  );
}

/**
 * The anthem row: plays the preview on a tap, or opens Spotify when there is no preview. WebKit
 * keeps a detached <audio> playing, so leaving pauses it and drops the source, and a play() that
 * lands after leaving is stopped on arrival.
 */
function IntroAnthem({ anthem }) {
  const audio = useRef(null);
  const live = useRef(true);
  const [playing, setPlaying] = useState(false);
  const hold = useCallback((el) => { if (el) audio.current = el; }, []);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      const el = audio.current;
      if (el) { el.pause(); el.removeAttribute('src'); el.load(); }
    };
  }, []);
  const toggle = () => {
    const el = audio.current;
    if (!el) { if (anthem.spotifyUrl) window.open(anthem.spotifyUrl, '_blank', 'noopener'); return; }
    if (!el.paused) { el.pause(); setPlaying(false); return; }
    setPlaying(true);
    Promise.resolve(el.play()).then(() => { if (!live.current) el.pause(); }).catch(() => setPlaying(false));
  };
  return (
    <button type="button" onClick={toggle} data-testid="intro-anthem" aria-pressed={playing} style={{ display: 'flex', alignItems: 'center', gap: 12, height: 60, padding: '0 18px 0 8px', border: 0, borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #262626', color: '#fff', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
      <span style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', background: '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{playing ? <path d="M6 4h4v16H6zM14 4h4v16h-4z" /> : <path d="M7 4v16l13-8z" />}</svg>
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '.14em', color: '#22ECFB' }}>ANTHEM · PLAYS ON YOUR FIRST TAP</span>
        <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{anthem.name} · {anthem.artist}</span>
      </span>
      <span aria-hidden="true" style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 3, height: 20 }}>
        {[8, 16, 11, 20, 13].map((h, i) => <span key={i} style={{ width: 3, height: h, background: '#22ECFB' }} />)}
      </span>
      {anthem.previewUrl && <audio ref={hold} src={anthem.previewUrl} loop preload="none" />}
    </button>
  );
}
